<template>
  <a-spin :spinning="!rights">
    <div class="w-full grid place-items-center rounded-[12px] bg-back-1">
      <div
        v-if="order.status === undefined"
        :class="{
          'w-full': true,
          flex: true,
          'flex-col': isGlobalVersion(),
        }"
      >
        <template v-if="rights">
          <FeatureInfo
            v-model:packageInfo="data.packageInfo"
            :feature-type="feature_type"
            :rights-name="rights.rights_name"
            :unit-price="feature_unit_price"
            :days="expiredDays"
          />
          <template v-if="!isGlobalVersion()">
            <div class="w-[1px] bg-[#f1f1f1]"></div>
            <PayQrcode
              v-model:pay-type="payType"
              v-model:agreement-checked="agreementChecked"
              :loading="creating"
              :qrcode="qrcode"
              :behavior="behavior"
              :price-text="amount"
            >
              <Consultant
                class="mt-[20px]"
                :rightType="rights.rights_type"
                @transfer="onTransferClick"
              />
            </PayQrcode>
          </template>
          <AirwallexOperation
            v-else
            mode="payment"
            :hostPaymentPage="hostPaymentPage"
            :loading="creating || !hostPaymentPage"
            @payed="handlePayed"
          >
          </AirwallexOperation>
        </template>
      </div>
      <div v-else-if="paySucc" class="flex-y-center w-full h-full">
        <PaySucc
          class="flex-1 py-[173px]"
          :title="paySuccTitle"
          :desc="paySuccDesc"
          @on-success="onCancel"
        />
        <div class="w-[1px] h-full bg-[#f1f1f1]"></div>
        <PaySuccTip class="max-w-[254px]"></PaySuccTip>
      </div>
      <PayFail v-else class="py-[173px]" @back="onRePay" />
    </div>
  </a-spin>
</template>

<script setup lang="ts" name="BuyRights">
import type { HostPaymentPage } from 'airwallex-payment-elements'
import useOrder, { useRights } from '../../useOrder'
import PayQrcode from '../pay-qrcode.vue'
import Consultant from '../consultant.vue'
import PaySucc from '../pay-succ.vue'
import PaySuccTip from '../pay-succ-tip.vue'
import PayFail from '../pay-fail.vue'
import FeatureInfo from './feature-info.vue'
import type { UserInfo } from '~/api/interface'
import { ORDER_BEHAVIOR, ORDER_TYPE, RIGHTS_FEATURE } from '~/api/interface'
import { RightsFeature } from '~/constants/rights'
import type { ICreditCoinsPackage } from '~/api'
import AirwallexOperation from '~/features/payment/AirwallexOperation.vue'

const props = defineProps<{
  user_info?: UserInfo
  feature_type: RIGHTS_FEATURE
  behavior: ORDER_BEHAVIOR
  order_type: ORDER_TYPE
  onCancel: () => void
}>()

const feature = computed(() => RightsFeature()[props.feature_type])
const expiredDays = computed(
  () => props.user_info?.rights_record.credit_coin_time
)

const onTransferClick = () => {
  createOrder(true)
}

const { rights, getRights } = useRights(props.user_info?.rights_type)

const userInfo = useUserInfo()

const addPrice = computed(
  () =>
    rights.value?.rights_by_time.find((v) => v.id === userInfo.value?.rights_id)
      ?.add_price
)

const data = ref<{
  behavior: ORDER_BEHAVIOR
  rights_type?: number
  order_type: ORDER_TYPE
  parent_order_id?: number
  packageInfo?: ICreditCoinsPackage
}>({
  behavior: props.behavior,
  rights_type: rights.value?.rights_type,
  order_type: props.order_type,
  parent_order_id: props.user_info?.current_order_id,
})

provide('rights', rights)

const { $t } = useNuxtApp()

// 权益单价
const feature_unit_price = computed(() => {
  if (!addPrice.value) {
    return 0
  }
  return addPrice.value[feature.value.priceKey]
})

const amount = computed(() => data.value.packageInfo?.price || 0)
const yuanFormat = computed(() => formatPrice(amount.value).string)

const paySuccTitle = computed(() => $t('jia-gou-cheng-gong'))
const paySuccDesc = computed(() =>
  $t(
    'rightsvaluerightsname-countvalue-unit-ge-featurevaluename-jia-gou-cheng-gong-cheng-gong-zhi-fu-yuanformatvalue',
    [
      rights.value?.rights_name,
      data.value.packageInfo?.total_credit_coins,
      feature.value.name,
      yuanFormat.value,
    ]
  )
)

const {
  agreementChecked,
  payType,
  qrcode,
  order,
  rawOrder,
  paySucc,
  onRePay,
  createOrder,
  creating,
} = useOrder(data)

const hostPaymentPage = computed(() => {
  if (!rawOrder.value) {
    return
  }

  const id = rawOrder.value.id

  const config: HostPaymentPage = {
    customer_id: rawOrder.value!.other_info.vendor_customer_id,
    currency: rawOrder.value!.currency,
    client_secret: rawOrder.value!.other_info.airwallex_client_secret,
    intent_id: rawOrder.value!.vendor_trade_no,
    successUrl: `${location.origin}/paymentResult?xmov_oid=${id}`,
    failUrl: `${location.origin}/paymentResult?xmov_oid=${id}`,
  }

  return config
})

const handlePayed = () => location.reload()

onMounted(getRights)
</script>
